import { z } from 'zod';

import {
  planTierSchema,
  subscriptionStatusSchema,
  type AdPlatform,
  type AutomationMode,
  type ConnectionStatus,
  type CreditEntryType,
  type Locale,
  type SubscriptionStatus,
  type WorkspaceRole,
} from './enums.js';
import type { PlanTier } from './plans.js';

/**
 * /admin panelinin FE/BE ortak sözleşmesi — Adım 1.
 *
 * ---------------------------------------------------------------------------
 * Panelin ne olduğu
 * ---------------------------------------------------------------------------
 * Admin paneli bir alet, vitrin değil. Buradaki her uç, BUGÜN verisi olan bir
 * tabloya bakıyor ya da bugün gerçekten yapılabilen bir müdahaleyi yapıyor.
 * Boş tabloya rapor ekranı yazmak panel değil, gürültü — bu yüzden kuyruk
 * ekranı (kuyruk yok), platform bağlantıları (bağlantı yok), gelir panosu
 * (üç abonelik de TRIAL) burada YOK. Onlar kendi adımlarında doğuyor.
 *
 * ---------------------------------------------------------------------------
 * 404, 403 değil
 * ---------------------------------------------------------------------------
 * Yetkisiz istek `NotFoundException` alır. 403 "burada bir şey var ama sana
 * kapalı" demek; panelin varlığı sızmamalı. Bu kural hem sayfaya hem uca
 * uygulanır ve KİMLİKSİZ isteği de kapsar: `/admin/*` uçları global
 * `JwtAuthGuard`'ı `@Public()` ile atlayıp kendi guard'ında 401 yerine 404
 * üretir, yoksa "401 aldım demek ki uç var" bilgisi sızar.
 *
 * ---------------------------------------------------------------------------
 * Kiracı sınırı bilerek aşılıyor
 * ---------------------------------------------------------------------------
 * Admin sorguları `workspaceId` filtresi TAŞIMAZ. Ürünün geri kalanında bu bir
 * hata olurdu; burada kasıtlı. İleride Prisma tenancy middleware'i gelirse
 * admin yolu açıkça muaf tutulmalı — kazara değil, yazarak.
 *
 * ---------------------------------------------------------------------------
 * Tarih ve para biçimi
 * ---------------------------------------------------------------------------
 * Yanıttaki her tarih ISO 8601 dizesi (`2026-08-10T09:00:00.000Z`). Prisma
 * `Date` nesnesi döndürüyor; JSON'a düşerken zaten dizeye çevriliyor, ama tip
 * tarafında `Date` demek frontend'i `new Date(x as unknown as string)` gibi
 * yalanlara zorluyordu.
 *
 * `Subscription.adSpendLimit` Prisma `Decimal`. HAM DÖNDÜRÜLMEZ — Fastify onu
 * `{s,e,d}` nesnesine çevirir. Sözleşme `number` diyor; servis `.toNumber()`
 * uygular. Değer aralığı (en fazla 50.000, ya da sınırsız için -1) çift
 * duyarlıklı sayının güvenli aralığının çok altında.
 */

// ────────────────────────────── Liste sözleşmesi ──────────────────────────────

/**
 * Sayfalama neden imleç (cursor) değil offset.
 *
 * İmleç büyük ve sürekli akan listelerde doğru seçim; admin listelerinde ise
 * yöneticinin "kaç kullanıcı var", "3. sayfaya git" sorusu imleçle
 * cevaplanamıyor. Toplam sayı zaten ekranın kendisi — sayfa numarası ve toplam
 * göstermek offset'i zorunlu kılıyor. Bugünkü büyüklükte (onlarca satır)
 * `OFFSET` maliyeti ölçülemez; on binlere çıkarsa bu sözleşme yeniden
 * değerlendirilir.
 */
export const ADMIN_PAGE_SIZE_DEFAULT = 25;
export const ADMIN_PAGE_SIZE_MAX = 100;

/// Sorgu dizesinden gelen boolean. `z.coerce.boolean()` KULLANILMAZ —
/// `"false"` boş olmayan bir dize olduğu için `true`ya çevrilir, sessiz hata.
const boolParam = z.enum(['true', 'false']).transform((v) => v === 'true');

/// Arama metni. Boş dizeye izin verilmiyor: `?q=` ile gelen istek "arama yok"
/// demek, `contains ''` değil.
const searchParam = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .optional()
  .transform((v) => (v && v.length > 0 ? v : undefined));

const sortDirSchema = z.enum(['asc', 'desc']).default('desc');

/// Tüm liste uçlarının ortak parçası. Sorgu dizesinden geldiği için sayılar
/// `coerce` ile çevriliyor — `?page=2` bir dize.
export const adminListQuerySchema = z.object({
  q: searchParam,
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(ADMIN_PAGE_SIZE_MAX)
    .default(ADMIN_PAGE_SIZE_DEFAULT),
});
export type AdminListQuery = z.infer<typeof adminListQuerySchema>;

/// Sayfalanmış yanıtın kabı. `pageCount` sunucuda hesaplanıyor ki arayüz
/// bölmeyi (ve sıfıra bölmeyi) tekrar düşünmesin.
export interface AdminPage<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

// ────────────────────────────── Workspace listesi ──────────────────────────────

export const adminWorkspaceListQuerySchema = adminListQuerySchema.extend({
  /// `name` ya da `slug` içinde geçen metin aranır (büyük/küçük harf duyarsız).
  plan: planTierSchema.optional(),
  status: subscriptionStatusSchema.optional(),
  /// Yalnızca üyesi olmayan workspace'ler. Gerçek bir durum, kenar durum
  /// değil: kullanıcı silinince `WorkspaceMember` cascade ile gidiyor ama
  /// `Workspace` kalıyor. Bugün DB'de bir tane var.
  ownerless: boolParam.optional(),
  sort: z.enum(['createdAt', 'name', 'members']).default('createdAt'),
  dir: sortDirSchema,
});
export type AdminWorkspaceListQuery = z.infer<typeof adminWorkspaceListQuerySchema>;

export interface AdminWorkspaceRow {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  memberCount: number;
  /// `WorkspaceMember.role = OWNER` olan ilk üyenin e-postası.
  /// **null olabilir** — sahipsiz workspace gerçek. `!` ile geçilmez.
  ownerEmail: string | null;
  /// Abonelik satırı olmayan workspace teoride mümkün (kayıt akışı dışında
  /// açılmış olabilir); alanlar bu yüzden nullable.
  plan: PlanTier | null;
  status: SubscriptionStatus | null;
  currentPeriodEnd: string | null;
  /// Kullanılabilir kredi = plan + topup − rezerve. Bakiye satırı yoksa null.
  availableCredits: number | null;
}

// ────────────────────────────── Workspace detayı ──────────────────────────────

export interface AdminSubscriptionDetail {
  plan: PlanTier;
  status: SubscriptionStatus;
  /// `Decimal` değil `number` — yukarıdaki "Tarih ve para biçimi" notuna bak.
  /// `-1` sınırsız demek; arayüz ham `-1` BASMAZ, "sınırsız" yazar.
  adSpendLimit: number;
  monthlyCredits: number;
  maxAdAccounts: number;
  maxMembers: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  graceUntil: string | null;
  cancelAtPeriodEnd: boolean;
  /**
   * Ödeme sağlayıcısındaki bir aboneliğe bağlı mı.
   *
   * Kimlik alanlarının kendisi (`providerCustomerId`, `providerSubscriptionId`)
   * yanıta GİRMEZ — panelde yapılacak bir işleri yok, sızdırılacak bir
   * değerleri var. Arayüzün tek ihtiyacı bu boolean: `true` ise plan ve limit
   * düzenleme kapalıdır, çünkü sağlayıcı webhook'u satırı ezer.
   *
   * ⚠️ Faz 8'de `stripeLinked` iken bu ada geçti; sağlayıcı iyzico oldu.
   */
  paymentLinked: boolean;
}

export interface AdminCreditSummary {
  planCredits: number;
  topupCredits: number;
  reservedCredits: number;
  /// plan + topup − reserved. Arayüz aynı aritmetiği ikinci kez yazmasın.
  available: number;
}

export interface AdminWorkspaceMemberRow {
  userId: string;
  email: string;
  name: string | null;
  role: WorkspaceRole;
  joinedAt: string;
}

export interface AdminLedgerRow {
  id: string;
  type: CreditEntryType;
  /// Harcamada negatif, yüklemede pozitif.
  amount: number;
  balanceAfter: number;
  description: string | null;
  referenceType: string | null;
  createdAt: string;
}

export interface AdminAuditRow {
  id: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  /// İşlemi yapan kullanıcının e-postası. Sistem kaydında ya da kullanıcı
  /// silinmişse null (`AuditLog.user` ilişkisi `onDelete: SetNull`).
  actorEmail: string | null;
  createdAt: string;
}

/**
 * Admin görünümündeki reklam hesabı satırı — Adım 4.
 *
 * 🔴 KİRACI YALITIMINI BİLEREK AŞAN BİR OKUMA. `AdAccount` satırları
 * `workspaceId` taşıyor ve normal uçlarda o filtre zorunlu; burada platform
 * yöneticisi başka birinin kiracısına bakıyor. Bu, admin panelinin tanımlı
 * tek istisnası ve servis tarafında AÇIKÇA yorumlanmak zorunda — kazara
 * görünmemeli.
 *
 * `AdAccountRow` (kiracının kendi gördüğü satır) ile aynı DEĞİL ve olmamalı:
 * oradaki alanlar kullanıcıya "hesabım bağlı mı" diyor, buradakiler desteğe
 * "veri geldi mi" diyor. Ortak alanları kopyalamak yerine ayrı bir tip olması
 * bilinçli — biri büyüdüğünde diğerinin de büyümesi gerekmiyor.
 */
export interface AdminAdAccountRow {
  id: string;
  connectionId: string;
  platform: AdPlatform;
  /// Platformdaki kimlik (`act_1580535206954491`). Destek bunu platformun
  /// kendi arayüzünde aratıyor; olmadan hiçbir çapraz kontrol yapılamaz.
  platformRef: string;
  name: string;
  currency: string;
  timezone: string;
  /// `AdAccount.status` — platformun bildirdiği durum (`active`, `REJECTED`…).
  status: string;
  syncEnabled: boolean;
  lastSyncedAt: string | null;
  /// Bağlantının kendi durumu. Hesap sağlıklı görünüp bağlantı kopuk olabilir.
  connectionStatus: ConnectionStatus;
  /**
   * DEPOLANAN VARLIK SAYAÇLARI — bu satırın var oluş sebebi.
   *
   * "Senkron çalıştı, hesap gerçekten boş" ile "senkron çalıştı ama hiçbir
   * şey yazmadı" arasındaki farkı gösteren tek veri. Bugün Meta hesabında
   * üçü de 0 ve bu bir boş ekran değil, TEŞHİSİN KENDİSİ: `lastSyncedAt`
   * dolu + sayaçlar sıfır = hesapta kampanya yok.
   */
  counts: { campaigns: number; adSets: number; ads: number };
  /// İlk ve son `MetricDaily` günü (`YYYY-MM-DD`). Hiç metrik yoksa ikisi de
  /// `null` — "veri geldi mi" sorusunun ikinci yarısı.
  metricRange: { from: string; to: string } | null;
  /**
   * Son senkron hatası (Redis'ten). `null` = kayıtlı hata yok.
   *
   * Sağlayıcı mesajı TEMİZLENMİŞ hâliyle taşınır; jeton parçası içermez.
   * `deadLettered` doluysa iş yeniden denenmeyecek demektir ve destek bunu
   * bilmeden "biraz bekleyin" der.
   */
  lastSyncError: {
    /**
     * 🔴 `SyncErrorRow['category']` ile AYNI BEŞ kova olmak zorunda.
     *
     * Burada bir süre yalnız dördü yazılıydı ve beşincisi — worker'ın YEDEK
     * kovası `'unknown'`, sınıflandırılamayan her hatanın düştüğü yer — dışarıda
     * kalmıştı. Değer çalışma zamanında yine geliyordu, yalnız tip yalan
     * söylüyordu: eşleyici onu `as` ile susturmak zorunda kalıyor, kovaya göre
     * dallanan bir `switch` yazan kişi eksik dalı derleyiciden ÖĞRENEMİYORDU.
     * `'transient'`e katlamak da çözüm değil — desteğe "geçici, düzelir"
     * dedirtirdi; sınıflandırılamamış bir hata hakkında bilinen tek şey
     * sınıflandırılamamış olduğu.
     */
    category: 'auth' | 'rate_limit' | 'transient' | 'data' | 'unknown';
    attempts: number;
    message: string;
    deadLettered: boolean;
    at: string;
  } | null;
}

export interface AdminWorkspaceDetail {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Otomasyon guardrail'leri — Adım 1'de **SALT OKUNUR**.
   *
   * Bunları admin'den değiştirmek "workspace guardrail'inin üstünde platform
   * tavanı" işi ve uygulama planında Adım 6'ya ait. Bugün onları okuyan tek
   * satır kod bile yok; düzenleme düğmesi koymak, çalıştığı sanılan bir
   * düğme koymak olurdu.
   */
  guardrails: {
    automationMode: AutomationMode;
    maxBudgetChangePercent: number;
    maxActionsPerDay: number;
  };
  /**
   * Bu çalışma alanının reklam hesapları — Adım 4'te eklendi.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden burada, neden ayrı bir ekran değil
   * ═══════════════════════════════════════════════════════════════════════
   * Adım 4 kampanya oluşturma sihirbazını açıyor: kullanıcı bizim panelden
   * DIŞ BİR PLATFORMDA gerçek nesne üretiyor. O andan itibaren desteğin en
   * sık alacağı çağrı "kampanyamı oluşturdum ama panelde göremiyorum" ve o
   * sorunun cevabı kampanya listesinde DEĞİL: senkron çalıştı mı, ne zaman
   * çalıştı, hata mı verdi, hesapta kaç kayıt duruyor.
   *
   * Çapraz kiracı bir kampanya listesi ekranı BİLEREK REDDEDİLDİ: müşteri
   * ekranının kopyası olurdu ve desteğe hiçbir kaldıraç vermezdi. Buradaki
   * sayaçlar veriyor — "senkron çalıştı, hesap boş" ile "senkron veri
   * yazmadı"yı ayıran tek şey onlar.
   */
  adAccounts: AdminAdAccountRow[];
  subscription: AdminSubscriptionDetail | null;
  credits: AdminCreditSummary | null;
  members: AdminWorkspaceMemberRow[];
  /// Son 10 defter satırı — tamamı Adım 8'in `/admin/credits` ekranında.
  recentLedger: AdminLedgerRow[];
  /// Bu workspace'e ait son 20 denetim kaydı. Tüm kiracıları kapsayan
  /// `/admin/audit` ekranı Adım 9'da.
  recentAudit: AdminAuditRow[];
}

// ─────────────────────────────── Kullanıcı listesi ───────────────────────────────

export const adminUserListQuerySchema = adminListQuerySchema.extend({
  /// `email` ya da `name` içinde geçen metin (büyük/küçük harf duyarsız).
  verified: boolParam.optional(),
  platformAdmin: boolParam.optional(),
  sort: z.enum(['createdAt', 'email']).default('createdAt'),
  dir: sortDirSchema,
});
export type AdminUserListQuery = z.infer<typeof adminUserListQuerySchema>;

export interface AdminUserRow {
  id: string;
  email: string;
  name: string | null;
  /// Doğrulama zamanı; null ise doğrulanmamış.
  emailVerified: string | null;
  isPlatformAdmin: boolean;
  locale: Locale;
  workspaceCount: number;
  /// `revokedAt` null VE `expiresAt > now` olan oturumlar.
  activeSessionCount: number;
  createdAt: string;
}

export interface AdminSessionRow {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  /// `revokedAt === null && expiresAt > now`. Sunucuda hesaplanıyor —
  /// istemcide `Date.now()` ile hesaplamak SSR/CSR uyuşmazlığı üretir.
  active: boolean;
  /// İsteği yapan yöneticinin KENDİ oturumu mu. Kendi oturumunu kapatmasın diye.
  current: boolean;
}

export interface AdminUserMembershipRow {
  workspaceId: string;
  workspaceName: string;
  workspaceSlug: string;
  role: WorkspaceRole;
  joinedAt: string;
}

export interface AdminUserDetail {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  locale: Locale;
  emailVerified: string | null;
  isPlatformAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  /// Parolası var mı. `passwordHash` ASLA yanıta girmez — sadece varlığı.
  /// false ise kullanıcı yalnızca sosyal girişle geliyor.
  hasPassword: boolean;
  identities: { provider: string; createdAt: string }[];
  memberships: AdminUserMembershipRow[];
  /// Tüm oturumlar (iptal edilmiş ve süresi dolmuş dahil), yeniden eskiye.
  /// `refreshTokenHash` yanıta GİRMEZ.
  sessions: AdminSessionRow[];
  /// Bu kullanıcının aktör olduğu son 20 denetim kaydı.
  recentAudit: AdminAuditRow[];
}

// ─────────────────────────────── Genel bakış ───────────────────────────────

/**
 * Ölçüm tanımları — ekrandaki her sayının `psql` karşılığı olmalı.
 *
 *  users.total            → users tablosundaki satır sayısı
 *  users.verified         → "emailVerified" IS NOT NULL
 *  users.newLast7d/30d    → "createdAt" >= now() - interval
 *  workspaces.ownerless   → hiç WorkspaceMember satırı olmayan workspace
 *  workspaces.subscribed  → aboneliği TRIALING ya da ACTIVE olan workspace
 *                           ("aktif workspace" bu demek; "son N günde giriş"
 *                           tanımı için şemada son giriş alanı yok)
 *  sessions.active        → "revokedAt" IS NULL AND "expiresAt" > now()
 */
export interface AdminOverviewUsers {
  total: number;
  verified: number;
  unverified: number;
  newLast7d: number;
  newLast30d: number;
}

export interface AdminOverviewWorkspaces {
  total: number;
  /// Hiç üyesi olmayanlar. Sıfırdan büyükse arayüz bunu uyarı olarak gösterir
  /// ve `/admin/workspaces?ownerless=true` filtresine bağlar.
  ownerless: number;
  /// Aboneliği TRIALING ya da ACTIVE olanlar.
  subscribed: number;
}

export interface AdminOverviewSessions {
  active: number;
  usersWithActiveSession: number;
}

/// Plan × durum kırılımı. Bugün tek satır (TRIAL/TRIALING/3) — bu yüzden
/// grafik değil tablo: tek dilimli pasta bilgi taşımaz.
export interface AdminSubscriptionBucket {
  plan: PlanTier;
  status: SubscriptionStatus;
  count: number;
}

export interface AdminOverviewCredits {
  planCredits: number;
  topupCredits: number;
  reservedCredits: number;
  /// Defterdeki pozitif satırların toplamı.
  grantedTotal: number;
  /// Defterdeki negatif satırların mutlak toplamı. **Bugün 0** — tüketim
  /// satırları kreatif üretimiyle (Adım 11) doğuyor. Arayüz bunu "veri yok"
  /// diye değil "henüz kredi harcaması yok" diye yazar; gösterge bozuk
  /// sanılmasın.
  spentTotal: number;
}

export interface AdminTrialEndingRow {
  workspaceId: string;
  name: string;
  slug: string;
  plan: PlanTier;
  status: SubscriptionStatus;
  currentPeriodEnd: string;
}

export interface AdminRecentUserRow {
  id: string;
  email: string;
  name: string | null;
  emailVerified: string | null;
  createdAt: string;
}

export interface AdminLedgerRowWithWorkspace extends AdminLedgerRow {
  workspaceId: string;
  workspaceName: string;
}

/**
 * Sistem sağlığı.
 *
 * `queues.registered` bugün 0 ve bu doğru bir sayı: `BullModule.forRoot` var
 * ama `registerQueue` yok, ilk gerçek kuyruk Adım 3'te doğuyor. Uydurma
 * kuyruk metriği basmak yerine sıfır basılıyor; arayüz "kuyruk yok — Adım
 * 3'te açılacak" der. Ayrıntılı kuyruk ekranı `/admin/queues` Adım 3'ün işi.
 */
export interface AdminHealth {
  database: 'up' | 'down';
  redis: 'up' | 'down';
  queues: { registered: number };
}

export interface AdminOverview {
  users: AdminOverviewUsers;
  workspaces: AdminOverviewWorkspaces;
  sessions: AdminOverviewSessions;
  subscriptions: AdminSubscriptionBucket[];
  credits: AdminOverviewCredits;
  /// `currentPeriodEnd` 7 günden yakın olanlar, en yakın önce, en fazla 10.
  trialsEndingSoon: AdminTrialEndingRow[];
  /// Son 10 kayıt.
  recentUsers: AdminRecentUserRow[];
  /// Son 10 defter satırı.
  recentLedger: AdminLedgerRowWithWorkspace[];
  health: AdminHealth;
  /// Yanıtın üretildiği an. Arayüz "şu kadar önce güncellendi" derken
  /// `Date.now()` yerine bunu kullanır.
  generatedAt: string;
}

// ─────────────────────────────── Yazma istekleri ───────────────────────────────

/**
 * Her yazma işlemi gerekçe ister.
 *
 * Zorunlu olması bilinçli: denetim kaydında "kim, ne, ne zaman" var ama
 * "neden" yok. Altı ay sonra `admin.credits.adjusted +500` satırına bakan kişi
 * bunun destek jesti mi hata telafisi mi olduğunu bilemez. Gerekçe
 * `AuditLog.after.reason` alanına yazılır.
 */
const adminReason = z
  .string()
  .trim()
  .min(3, 'Gerekçe yazın (en az 3 karakter)')
  .max(500, 'Gerekçe en fazla 500 karakter olabilir');

/// Sınırsız limitin sayısal karşılığı (`plans.ts` ile aynı sözleşme).
export const ADMIN_UNLIMITED = -1;

const limitValue = z
  .number()
  .int('Tam sayı girin')
  .min(ADMIN_UNLIMITED, 'Sınırsız için -1, aksi hâlde 0 veya üzeri')
  .max(1_000_000)
  .refine((v) => v === ADMIN_UNLIMITED || v >= 0, 'Sınırsız için -1, aksi hâlde 0 veya üzeri');

/**
 * Plan elle değiştirme.
 *
 * `resetLimits` neden var: tasarım gereği `Subscription` limitleri plan
 * tablosundan KOPYALANIYOR (§7) — plan değişince limitler kendiliğinden
 * değişmez. Varsayılan `true`, yani "planı değiştir" beklendiği gibi çalışır.
 * `false` seçeneği pazarlıklı hesap içindir: plan etiketi değişsin, elle
 * verilmiş limitler korunsun.
 *
 * Dönem tarihleri (`currentPeriodStart/End`) ve kredi yüklemesi bu uçta
 * DEĞİŞMEZ. §8 "kredi dönem başında yüklenir" diyor; dönem ortasında elle
 * yapılan plan değişiminde kredi farkı yüklemek tanımsız bir davranış olurdu.
 * Kredi gerekiyorsa `POST /admin/workspaces/:id/credits` ile ayrıca ve
 * defterde görünür biçimde verilir.
 */
export const adminPlanChangeSchema = z.object({
  plan: planTierSchema,
  resetLimits: z.boolean().default(true),
  reason: adminReason,
});
export type AdminPlanChangeInput = z.infer<typeof adminPlanChangeSchema>;

/// Limitleri plan tablosundan bağımsız ezme (Enterprise / pazarlıklı hesap).
export const adminLimitsSchema = z.object({
  /// USD. `Decimal(14,2)` kolonuna yazılıyor; iki ondalıktan fazlası reddedilir.
  adSpendLimit: z
    .number()
    .min(ADMIN_UNLIMITED)
    .max(99_999_999_999.99)
    .refine((v) => v === ADMIN_UNLIMITED || v >= 0, 'Sınırsız için -1, aksi hâlde 0 veya üzeri')
    .refine(
      (v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6,
      'En fazla iki ondalık basamak girin',
    ),
  monthlyCredits: limitValue,
  maxAdAccounts: limitValue,
  maxMembers: limitValue,
  reason: adminReason,
});
export type AdminLimitsInput = z.infer<typeof adminLimitsSchema>;

/**
 * Abonelik durumunu değiştirme.
 *
 * ⚠️ Bu uç "workspace askıya alma" DEĞİLDİR. Şemada workspace'i askıya alacak
 * bir alan yok ve olsaydı bile bugün onu okuyan hiçbir guard yok — düğme
 * yalan söylerdi. Değiştirilen şey aboneliğin faturalama durumu; arayüz
 * bunun neyi durdurup neyi durdurmadığını AÇIKÇA yazar (bugün: hiçbir şeyi
 * durdurmuyor, çünkü durumu okuyan kod Adım 8'de yazılacak).
 */
export const adminSubscriptionStatusSchema = z
  .object({
    status: subscriptionStatusSchema,
    /// Yalnızca `GRACE` için anlamlı; diğer durumlarda null'a çekilir.
    graceUntil: z.iso.datetime().nullable().default(null),
    reason: adminReason,
  })
  .refine((v) => v.status !== 'GRACE' || v.graceUntil !== null, {
    message: 'GRACE durumu ek süre bitiş tarihi ister',
    path: ['graceUntil'],
  });
export type AdminSubscriptionStatusInput = z.infer<typeof adminSubscriptionStatusSchema>;

/// Deneme/dönem sonunu kaydırma. Negatif değer kısaltır — destek yanlış
/// verilmiş uzatmayı geri alabilmeli.
export const adminTrialExtendSchema = z.object({
  days: z
    .number()
    .int()
    .min(-365)
    .max(365)
    .refine((v) => v !== 0, 'Sıfır gün bir değişiklik değil'),
  reason: adminReason,
});
export type AdminTrialExtendInput = z.infer<typeof adminTrialExtendSchema>;

/**
 * Elle kredi verme / geri alma.
 *
 * Hangi kovaya yazılıyor: **topupCredits**. Gerekçe: `planCredits` dönem
 * sonunda silinir (§8, devretmez); destek jesti olarak verilen kredinin
 * kullanıcının haberi olmadan buharlaşması kötü bir sürpriz olur.
 *
 * Negatif düzeltmede önce `topupCredits`, yetmezse `planCredits` düşülür ve
 * bakiye ASLA negatife inmez — yetersizse istek `INSUFFICIENT_CREDITS` ile
 * reddedilir, kısmi düşüm yapılmaz.
 *
 * Defter satırı: tek `ADJUSTMENT` kaydı, `balanceAfter = planCredits +
 * topupCredits` (işlem sonrası, rezerve DAHİL DEĞİL — rezerve kilitli kredi,
 * bakiyeden ayrı bir kova). `referenceType = 'admin'`, `referenceId` = işlemi
 * yapan yöneticinin kullanıcı kimliği. Defter satırı asla güncellenmez.
 */
export const adminCreditAdjustSchema = z.object({
  amount: z
    .number()
    .int('Kredi tam sayı')
    .min(-100_000)
    .max(100_000)
    .refine((v) => v !== 0, 'Sıfır bir düzeltme değil'),
  reason: adminReason,
});
export type AdminCreditAdjustInput = z.infer<typeof adminCreditAdjustSchema>;

/**
 * Platform yöneticisi yetkisi verme / alma.
 *
 * İki kilitlenme koruması sunucuda uygulanır, arayüze bırakılmaz:
 *   1. Yönetici KENDİ kaydını düşüremez (`SELF_DEMOTION_FORBIDDEN`).
 *   2. Sistemdeki son platform yöneticisi düşürülemez (`LAST_PLATFORM_ADMIN`).
 * İkisi de olmazsa panele bir daha kimse giremez ve tek çıkış `psql` olur.
 */
export const adminPlatformAdminSchema = z.object({
  isPlatformAdmin: z.boolean(),
  reason: adminReason,
});
export type AdminPlatformAdminInput = z.infer<typeof adminPlatformAdminSchema>;

/**
 * E-postayı elle doğrulanmış işaretleme.
 *
 * Neden var: SMTP bugün kapalı ("525 5.7.13 Error: SMTP disabled"), doğrulama
 * postası gitmiyor. Doğrulanmamış bir kullanıcının sıkıştığı yerden çıkması
 * için destek elinde çalışan TEK yol bu. "Doğrulama e-postasını tekrar gönder"
 * düğmesi bilinçli olarak yok — çalışmış görünüp mail göndermeyen bir düğme
 * desteğe yalan söyler.
 */
export const adminReasonOnlySchema = z.object({ reason: adminReason });
export type AdminReasonOnlyInput = z.infer<typeof adminReasonOnlySchema>;

// ─────────────────────── Adım 4 — reklam hesabı müdahalesi ───────────────────────

/**
 * `POST /admin/ad-accounts/:id/sync` — admin adına elle senkron.
 *
 * Gövde yalnız gerekçe taşıyor: hangi hesap yoldan geliyor, ne yapılacağı
 * sabit. Gerekçe ZORUNLU çünkü bu çağrı kiracının soğuma kilidini aşıyor ve
 * "neden aştık" sorusunun kayıtta bir cevabı olmalı.
 *
 * ⚠️ Yanıt "senkron BİTTİ" demez, "kuyruğa GİRDİ" der. İşi worker yapıyor ve
 * bitişi beklemek isteği dakikalarca açık tutardı; ekran `lastSyncedAt`
 * değişene kadar "kuyrukta" gösterir.
 */
export const adminAdAccountSyncSchema = adminReasonOnlySchema;
export type AdminAdAccountSyncInput = AdminReasonOnlyInput;

/**
 * `PATCH /admin/ad-accounts/:id` — senkronu aç/kapa.
 *
 * 🔴 YIKICI. Kapatmak, kiracının panelindeki her sayının sessizce eskimesi
 * demek; hiçbir hata mesajı çıkmaz, yalnız veri durur. Onay diyaloğu ve
 * gerekçe bu yüzden zorunlu, denetim satırı bu yüzden ayrı bir eylem.
 */
export const adminAdAccountUpdateSchema = z.object({
  syncEnabled: z.boolean(),
  reason: adminReason,
});
export type AdminAdAccountUpdateInput = z.infer<typeof adminAdAccountUpdateSchema>;

/**
 * İki ucun da yanıtı: GÜNCEL hesap satırı.
 *
 * Tüm workspace detayını geri vermek (diğer admin yazma uçlarının deseni)
 * burada gereksiz büyük olurdu — sayaçlar ve denetim listesi değişmiyor,
 * yalnız bir satır değişiyor. Arayüz o satırı yerinde günceller.
 */
export interface AdminAdAccountMutationResponse {
  account: AdminAdAccountRow;
  /// Senkron ucu için: kuyruğa giren işin kimliği. Aç/kapa ucunda `null`.
  syncJobId: string | null;
}

// ─────────────────────────────── Yazma yanıtları ───────────────────────────────

/// Yazma uçları güncel detayı geri verir: arayüz ikinci bir GET atmasın ve
/// "kaydettim ama ekran eski" durumu oluşmasın.
export type AdminWorkspaceMutationResponse = AdminWorkspaceDetail;
export type AdminUserMutationResponse = AdminUserDetail;

export interface AdminRevokeSessionsResponse {
  user: AdminUserDetail;
  /// Bu istekte gerçekten kapatılan oturum sayısı (zaten kapalı olanlar hariç).
  revokedCount: number;
}

// ─────────────────────────────── Hata kodları ───────────────────────────────

/// Arayüz `code` üzerinden dallanır; `message` yalnızca gösterim içindir.
export const AdminErrorCode = {
  /// Workspace ya da kullanıcı bulunamadı. HTTP 404.
  NOT_FOUND: 'NOT_FOUND',
  /// Workspace'in abonelik satırı yok — plan/limit/durum değiştirilemez.
  SUBSCRIPTION_MISSING: 'SUBSCRIPTION_MISSING',
  /// Workspace'in kredi bakiyesi satırı yok — düzeltme yazılamaz.
  CREDIT_BALANCE_MISSING: 'CREDIT_BALANCE_MISSING',
  /// Negatif düzeltme bakiyeyi eksiye düşürürdü.
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  /// Abonelik ödeme sağlayıcısına bağlı; elle değişiklik webhook tarafından
  /// ezilir. (Faz 8'e kadar adı `STRIPE_LINKED` idi — sağlayıcı iyzico oldu
  /// ve kod adının bir marka taşıması, ikinci sağlayıcıda yalan söylerdi.)
  PAYMENT_PROVIDER_LINKED: 'PAYMENT_PROVIDER_LINKED',
  /// Yönetici kendi platform yetkisini alamaz.
  SELF_DEMOTION_FORBIDDEN: 'SELF_DEMOTION_FORBIDDEN',
  /// Son platform yöneticisi düşürülemez.
  LAST_PLATFORM_ADMIN: 'LAST_PLATFORM_ADMIN',
  /// Yönetici kendi açık oturumunu bu ekrandan kapatamaz (çıkış için /auth/logout).
  SELF_SESSION_REVOKE_FORBIDDEN: 'SELF_SESSION_REVOKE_FORBIDDEN',
  /// E-posta zaten doğrulanmış.
  ALREADY_VERIFIED: 'ALREADY_VERIFIED',
} as const;
export type AdminErrorCode = (typeof AdminErrorCode)[keyof typeof AdminErrorCode];

// ─────────────────────────────── Denetim sözlüğü ───────────────────────────────

/**
 * Denetim kaydı `action` değerlerinin TEK kaynağı.
 *
 * Dize olarak elle yazılsalardı Adım 9'daki `/admin/audit` ekranı bugünkü
 * satırların bir kısmını tanımazdı — yazım farkı geçmişe dönük düzeltilemez.
 * `admin.` öneki kiracının kendi kayıtlarından (`workspace.created`,
 * `budget.updated`) ayırt etmek için.
 */
export const ADMIN_AUDIT_ACTION = {
  PLAN_CHANGED: 'admin.subscription.plan_changed',
  LIMITS_UPDATED: 'admin.subscription.limits_updated',
  STATUS_CHANGED: 'admin.subscription.status_changed',
  TRIAL_EXTENDED: 'admin.subscription.trial_extended',
  CREDITS_ADJUSTED: 'admin.credits.adjusted',
  PLATFORM_ADMIN_GRANTED: 'admin.user.platform_admin_granted',
  PLATFORM_ADMIN_REVOKED: 'admin.user.platform_admin_revoked',
  EMAIL_MARKED_VERIFIED: 'admin.user.email_marked_verified',
  SESSION_REVOKED: 'admin.session.revoked',
  SESSIONS_REVOKED_ALL: 'admin.session.revoked_all',

  // ── Faz 1'de eklenenler ────────────────────────────────────────────────
  /// Bozuk bir bağlantıyı kiracı adına kesme (destek senaryosu). Bu eylem
  /// kiracı scoping'ini BİLEREK aşar ve jeton kasasını temizler.
  CONNECTION_FORCE_DISCONNECTED: 'admin.connection.force_disconnected',
  /// Sahipsiz workspace'e sahip atama. Bugün DB'de bir tane var ve panel
  /// onu görebiliyor ama düzeltemiyordu.
  WORKSPACE_OWNER_ASSIGNED: 'admin.workspace.owner_assigned',
  /// Destek/GDPR gerekçesiyle workspace silme — cascade ile 20+ tablo gider.
  WORKSPACE_DELETED: 'admin.workspace.deleted',
  /// Takılı bir render işini zorla başarısız işaretleme + kredi iadesi.
  CREATIVE_JOB_FORCE_FAILED: 'admin.creative_job.force_failed',
  /// Kuyruk müdahalesi (yeniden deneme / iş kaldırma / dead-letter temizleme).
  QUEUE_JOB_RETRIED: 'admin.queue.job_retried',
  QUEUE_JOB_REMOVED: 'admin.queue.job_removed',

  // ── Faz 3'te eklenenler ────────────────────────────────────────────────
  /// Otomasyon şablonu düzenleme. Katalog PLATFORM GENELİ: bir düzenleme tüm
  /// kiracıların göreceği galeriyi değiştiriyor ve denetim kaydı olmadan
  /// "bu eşiği kim değiştirdi" sorusu cevapsız kalırdı.
  ///
  /// ⚠️ KURULMUŞ kuralları etkilemiyor — kurulum şablonun gövdesini kurala
  /// kopyalıyor (`templates.service.ts` → `update`).
  TEMPLATE_UPDATED: 'admin.template.updated',

  // ── Faz 8'de eklenenler ────────────────────────────────────────────────
  /// Plan kataloğu düzenleme (`plan_catalog`). Fiyat ve kota PLATFORM GENELİ;
  /// "bu fiyatı kim değiştirdi" sorusunun kayıtta bir cevabı olmalı.
  ///
  /// ⚠️ MEVCUT abonelikleri etkilemiyor — limitler abonelik satırına
  /// açılırken kopyalanıyor (`plan-catalog.service.ts` başındaki tabloya bak).
  PLAN_CATALOG_UPDATED: 'admin.plan_catalog.updated',
  /// Planı satıştan kaldırma / geri alma. Fiyat düzenlemesinden AYRI kayıt:
  /// biri rakam değiştiriyor, diğeri planı yeni müşterilere kapatıyor.
  PLAN_CATALOG_PUBLISH_TOGGLED: 'admin.plan_catalog.publish_toggled',

  // ── 18 Ağu 2026 — kurumsal WhatsApp ────────────────────────────────────
  /// Kurumsal WhatsApp numarasının QR ile bağlanması. Kiracıya ait DEĞİL ve
  /// tam da bu yüzden kaydediliyor: bağlanan tek numara BÜTÜN kiracıların
  /// bildirim yolu ve "bu numarayı kim bağladı" sorusu bir gün sorulacak.
  PLATFORM_MESSAGING_CONNECTED: 'admin.platform_messaging.connected',
  /// Kesme. Bağlamadan AYRI kayıt çünkü sonucu asimetrik: bağlamak bir yolu
  /// açıyor, kesmek bütün kiracıların WhatsApp bildirimini anında durduruyor.
  PLATFORM_MESSAGING_DISCONNECTED: 'admin.platform_messaging.disconnected',

  // ── Adım 4 — reklam hesabı teşhisi ─────────────────────────────────────
  /**
   * Admin adına elle senkron tetikleme.
   *
   * Kiracının kendi ucundaki 15 dakikalık soğuma kilidini (`SYNC_TOO_SOON`)
   * BİLEREK aşıyor: destek, bağlantı onarıldıktan sonra verinin aktığını
   * müşteriyi aramadan görebilmeli. Kiracının kendi `account.sync_requested`
   * satırından AYRI bir eylem — "kim istedi" sorusunun cevabı farklı.
   */
  AD_ACCOUNT_SYNC_REQUESTED: 'admin.ad_account.sync_requested',
  /**
   * Hesabın senkronunu açma/kapama. YIKICI: kapatmak veri akışını durdurur ve
   * kiracının paneli sessizce eskimeye başlar — onay + gerekçe zorunlu.
   *
   * Bugün canlı bir gerekçesi var: X (TWITTER) hesabı `REJECTED` durumda ama
   * `syncEnabled: true` — her turda boşuna senkronlanıyor.
   */
  AD_ACCOUNT_SYNC_TOGGLED: 'admin.ad_account.sync_toggled',
} as const;
export type AdminAuditAction = (typeof ADMIN_AUDIT_ACTION)[keyof typeof ADMIN_AUDIT_ACTION];

export const ADMIN_TARGET_TYPE = {
  WORKSPACE: 'workspace',
  SUBSCRIPTION: 'subscription',
  CREDIT_BALANCE: 'credit_balance',
  USER: 'user',
  SESSION: 'session',
  CONNECTION: 'connection',
  CREATIVE_JOB: 'creative_job',
  QUEUE: 'queue',
  /// Faz 3 — otomasyon şablonu (`workflow_templates`). Kiracıya ait DEĞİL.
  WORKFLOW_TEMPLATE: 'workflow_template',
  /// Faz 8 — plan kataloğu satırı (`plan_catalog`). Kiracıya ait DEĞİL;
  /// `targetId` plan kademesi (`STARTER`, `GROWTH`…).
  PLAN_CATALOG: 'plan_catalog',
  /// 18 Ağu 2026 — platform geneli mesajlaşma bağlantısı
  /// (`platform_messaging_connections`). `targetId` kanal adı (`WHATSAPP`).
  PLATFORM_MESSAGING: 'platform_messaging',
  /// Adım 4 — reklam hesabı (`ad_accounts`). `targetId` bizim UUID'imiz,
  /// platform kimliği (`act_…`) DEĞİL: denetim kaydı bir gün platform
  /// kimliği değişse bile aynı satırı işaret etmeli.
  AD_ACCOUNT: 'ad_account',
} as const;
export type AdminTargetType = (typeof ADMIN_TARGET_TYPE)[keyof typeof ADMIN_TARGET_TYPE];

// ─────────────────────────── Çapraz kiracı denetim ───────────────────────────

/**
 * `/admin/audit` (Adım 9) — TÜM kiracıları kapsayan denetim kaydı.
 *
 * Bugün DB'de 67 satır var; panelin en olgun veri kaynağı. Kiracı tarafı
 * eylemleri `common.ts`'teki `AUDIT_ACTION` sözlüğünden, admin eylemleri
 * `ADMIN_AUDIT_ACTION`'dan geliyor — ekran ikisini de tanır.
 */
export const adminAuditListQuerySchema = adminListQuerySchema.extend({
  workspaceId: z.uuid().optional(),
  actorUserId: z.uuid().optional(),
  action: z.string().max(80).optional(),
  targetType: z.string().max(40).optional(),
  /// `admin.` önekli olanlar / kiracı eylemleri / hepsi.
  origin: z.enum(['admin', 'tenant', 'all']).default('all'),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type AdminAuditListQuery = z.infer<typeof adminAuditListQuerySchema>;

export interface AdminAuditRowFull extends AdminAuditRow {
  /// `null` = PLATFORM işlemi, bir kiracıya ait değil (yetki verme/alma,
  /// oturum kapatma, e-posta doğrulama). Arayüz bu satırları "Platform"
  /// etiketiyle gösteriyor; kiracı sütununu boş bırakmıyor.
  workspaceId: string | null;
  workspaceName: string | null;
  actorUserId: string | null;
  ip: string | null;
  userAgent: string | null;
  /// Öncesi/sonrası — sır alanları temizlenmiş.
  before: unknown | null;
  after: unknown | null;
}
